import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";
import { Schemas } from "../types/api-types";

export type MobileCustomer = Schemas['MobileCustomerResponse'];
export type CustomerResponse = Schemas['CustomerResponse'] & {
    is_nic_verified?: number | null;
    ci_customer_nic_id?: number | null;
    nominees?: Array<{
        id: number;
        name_ln1?: string;
        name_ln2?: string;
        name_ln3?: string;
        relationship?: string;
        reg_id_num?: string;
    }>;
};
export type BranchLookup = Schemas['BranchLookupResponse'];
export type CustomerUpdate = Schemas['CustomerUpdate'];
export type CustomerReject = Schemas['CustomerReject'];
export type CustomerCreate = Schemas['CustomerCreate'];
export type PaginatedCustomerListResponse<T> = Schemas['PaginatedCustomerListResponse'] & { items: T[] };
export type CustomerTypeLookup = Schemas['CustomerTypeResponse'];
export type CustomerListDetail = Schemas['CustomerListDetail'];
export type CiCity = Schemas['CiCityResponse'];
export type DeactivateOption = Schemas['DeactivateOptionResponse'];
export type PlAccountResponse = Schemas['PlAccountResponse'];
export type CustomerSmsNote = {
    id: number;
    ci_customer_id?: number | null;
    type?: 'sms' | 'note' | null;
    text?: string | null;
    status?: number | null;
    c_at?: string | null;
    c_by?: number | null;
};


export async function getMobileCustomers(filters: {
    customer_number?: string;
    branch_id?: number;
    status?: number | string;
} = {}): Promise<CustomerListDetail[]> {
    try {
        const response: ApiResponse<CustomerListDetail[]> = (
            await apiClient.get('/customer/', { params: filters })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customers');
    }
}

export async function getMobileCustomersPaginated(filters: {
    customer_number?: string;
    branch_id?: number;
    status?: number | string;
    customer_type_id?: number;
    page?: number;
    page_size?: number;
} = {}): Promise<PaginatedCustomerListResponse<CustomerListDetail>> {
    try {
        const response: ApiResponse<PaginatedCustomerListResponse<CustomerListDetail>> = (
            await apiClient.get('/customer/', { params: { ...filters, page: filters.page || 1, page_size: filters.page_size || 10 } })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customers');
    }
}

export async function createCustomer(payload: Partial<MobileCustomer>): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.post('/customer/', payload)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to create customer');
    }
}

export async function approveCustomer(customerNumber: string): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.put(`/customer/${customerNumber}/approve`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, `Failed to approve customer ${customerNumber}`);
    }
}

export async function rejectCustomer(customerNumber: string, payload: { deactivate_id: number; inactive_reason: string }): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.put(`/customer/${customerNumber}/reject`, payload)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, `Failed to reject customer ${customerNumber}`);
    }
}

export async function updateCustomer(customerNumber: string, payload: Partial<MobileCustomer>): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.put(`/customer/${customerNumber}`, payload)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, `Failed to update customer ${customerNumber}`);
    }
}

export async function getBranchLookup(): Promise<BranchLookup[]> {
    try {
        const response: ApiResponse<BranchLookup[]> = (
            await apiClient.get('/customer/branches')
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve branches');
    }
}

export async function getCustomerDetails(customerNumber: string): Promise<CustomerResponse> {
    try {
        const response: ApiResponse<CustomerResponse> = (
            await apiClient.get(`/customer/${customerNumber}`, { params: { allow_inactive: true } })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, `Failed to retrieve customer ${customerNumber}`);
    }
}

export async function getCustomerAccounts(customerId: number, balanceFilter?: 'positive' | 'zero', showDormant: boolean = false, showInactive: boolean = false): Promise<PlAccountResponse[]> {
    try {
        const params: Record<string, string | boolean> = {};
        if (balanceFilter) params.balance_filter = balanceFilter;
        if (showDormant) params.show_dormant = true;
        if (showInactive) params.show_inactive = true;
        const response: ApiResponse<PlAccountResponse[]> = (
            await apiClient.get(`/customer/${customerId}/all_accounts`, { params })
        ).data;
        return response.data || [];
    } catch (error: any) {
        handleApiError(error, `Failed to retrieve accounts for customer ${customerId}`);
    }
}

export async function getCustomerTypes(): Promise<CustomerTypeLookup[]> {
    try {
        const response: ApiResponse<CustomerTypeLookup[]> = (
            await apiClient.get('/customer/customer-types')
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer types');
    }
}

export async function getCiCities(): Promise<CiCity[]> {
    try {
        const response: ApiResponse<CiCity[]> = (
            await apiClient.get('/customer/ci-cities')
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve ci cities');
    }
}

export async function createCiCity(district: string, city: string): Promise<CiCity> {
    try {
        const response: ApiResponse<CiCity> = (
            await apiClient.post('/customer/ci-cities', { district, city_ln1: city })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to create city');
    }
}

export async function getDeactivateOptions(): Promise<DeactivateOption[]> {
    try {
        const response: ApiResponse<DeactivateOption[]> = (
            await apiClient.get('/customer/deactivate-options')
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve deactivate options');
    }
}

export async function getCustomerTypeInfoFields(customerTypeId: number): Promise<any[]> {
    try {
        const response: ApiResponse<any[]> = (
            await apiClient.get(`/customer/customer-type/${customerTypeId}/info`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer type info fields');
    }
}

export async function getCustomerTypeDetail(customerTypeId: number): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.get(`/customer/customer-type/${customerTypeId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer type details');
    }
}

export async function getCustomerTypeDocs(customerTypeId: number): Promise<any[]> {
    try {
        const response: ApiResponse<any[]> = (
            await apiClient.get(`/customer/customer-type/${customerTypeId}/docs`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer type documents');
    }
}

export async function createCustomerInfo(customerId: number, infoData: { ci_customer_type_info_id: number; file: string }): Promise<any> {
    try {
        const formData = new FormData();
        formData.append('ci_customer_type_info_id', infoData.ci_customer_type_info_id.toString());
        formData.append('file', infoData.file);
        
        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerId}/info`, formData)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to create customer info');
    }
}

export async function uploadCustomerDocument(customerId: number, docData: { ci_customer_type_doc_id: number; file: File }): Promise<any> {
    try {
        const formData = new FormData();
        formData.append('ci_customer_type_doc_id', docData.ci_customer_type_doc_id.toString());
        formData.append('file', docData.file);
        
        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerId}/docs/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to upload customer document');
    }
}

export async function uploadCustomerPhoto(customerNumber: string, photo: File): Promise<any> {
    try {
        const formData = new FormData();
        formData.append('photo', photo);
        
        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerNumber}/photo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to upload customer photo');
    }
}

export async function uploadCustomerSignature(customerNumber: string, signature: File): Promise<any> {
    try {
        const formData = new FormData();
        formData.append('signature', signature);
        
        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerNumber}/signature`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to upload customer signature');
    }
}

export async function uploadCustomerTax(customerId: number, taxData: { file: File; tin_number?: string; valid_until?: string; tax_latter?: string }): Promise<any> {
    try {
        const formData = new FormData();
        if (taxData.file) formData.append('file', taxData.file);
        if (taxData.tin_number) formData.append('tin_number', taxData.tin_number);
        if (taxData.valid_until) formData.append('valid_until', taxData.valid_until);
        if (taxData.tax_latter) formData.append('tax_latter', taxData.tax_latter);

        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerId}/tax`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to upload customer tax info');
    }
}

export async function getCustomerTaxInfo(customerId: number): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.get(`/customer/${customerId}/tax`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, `Failed to retrieve tax info for customer ${customerId}`);
    }
}

export async function getCustomerInfo(customerId: number): Promise<any[]> {
    try {
        const response: ApiResponse<any[]> = (
            await apiClient.get(`/customer/${customerId}/info`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer info');
    }
}

export async function getCustomerDocuments(customerId: number): Promise<any[]> {
    try {
        const response: ApiResponse<any[]> = (
            await apiClient.get(`/customer/${customerId}/docs`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer documents');
    }
}

export async function getCustomerNominee(customerId: number): Promise<any | null> {
    try {
        const response: ApiResponse<any | null> = (
            await apiClient.get(`/customer/${customerId}/nominee`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer nominee');
    }
}

export async function sendCustomerSms(customerId: number, message: string): Promise<boolean> {
    try {
        const response: ApiResponse<boolean> = (
            await apiClient.post(`/customer/${customerId}/sms`, { message })
        ).data;
        if (!response.data) {
            throw new Error(response.message || 'Failed to send SMS');
        }
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to send SMS');
    }
}

export async function addCustomerNote(customerId: number, message: string): Promise<boolean> {
    try {
        const response: ApiResponse<boolean> = (
            await apiClient.post(`/customer/${customerId}/note`, { message })
        ).data;
        if (!response.data) {
            throw new Error(response.message || 'Failed to add note');
        }
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to add note');
    }
}

export async function getCustomerSmsNotes(
    customerId: number,
    noteType?: 'sms' | 'note'
): Promise<CustomerSmsNote[]> {
    try {
        const response: ApiResponse<CustomerSmsNote[]> = (
            await apiClient.get(`/customer/${customerId}/sms-notes`, {
                params: noteType ? { note_type: noteType } : undefined
            })
        ).data;
        return response.data || [];
    } catch (error: any) {
        handleApiError(error, 'Failed to retrieve customer SMS and notes');
    }
}

export async function upsertCustomerNominee(
    customerId: number,
    nomineeData: {
        name_ln1: string;
        name_ln2?: string | null;
        name_ln3?: string | null;
        relationship?: string | null;
        reg_id_num?: string | null;
        date_of_birth?: string | null;
    }
): Promise<any> {
    try {
        const response: ApiResponse<any> = (
            await apiClient.post(`/customer/${customerId}/nominee`, nomineeData)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, 'Failed to save customer nominee');
    }
}

function handleApiError(error: any, defaultMessage: string): never {
    if (error.response) {
        const detail = error.response.data.detail;

        if (Array.isArray(detail)) {
            const messages = detail.map((err: any) => {
                const field = err.loc?.[err.loc.length - 1];
                return `${field}: ${err.msg}`;
            });

            throw new Error(messages.join(", "));
        }

        if (typeof detail === "string") throw new Error(detail);

        throw new Error(defaultMessage);
    }

    throw new Error(defaultMessage);
}
